import React, { MouseEventHandler, useRef } from 'react';
import { Image } from '../../../commonTypes/image';
import { CallToAction, TextLink } from '../../../commonTypes/links';
import colors from '../../../helpers/colors';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { useFocus, useHover } from '../../../hooks/useElementState';
import { applyComponentTheme } from '../../../theme';
import { PrimaryCta, SecondaryCta } from '../../Cta';
import Icon from '../../Icon';
import {
  Body,
  CtaWrapper,
  Heading,
  HeadingLink,
  IconWrapper,
  ProductCardContainer,
  SecondaryCtaWrapper,
  SecondaryLink,
  StyledImage,
} from './styles';

export type ProductCardProps = {
  icon?: string;
  image?: Image;
  heading: string;
  headingTag?: 'h2' | 'h3' | undefined;
  headingLink?: {
    url: string;
    onClick?: MouseEventHandler;
  };
  body?: string;
  cta: CallToAction;
  link?: TextLink;
  secondaryCta?: CallToAction;
  displayBackground?: boolean;
} & ComponentProps;

const ProductCard: React.FC<ProductCardProps> = ({
  icon,
  image,
  heading,
  headingTag = 'h2',
  headingLink,
  body,
  cta,
  link,
  secondaryCta,
  displayBackground,
  ...props
}) => {
  const ctaRef = useRef<HTMLAnchorElement>(null);
  const isHovering = useHover(ctaRef);
  const isFocused = useFocus(ctaRef);

  // If a heading link is passed in, it is applied to the heading.  If not, we apply the CTA url to the heading.
  const headingBehaviour = headingLink ?? cta;

  return (
    <ProductCardContainer
      displayBackground={displayBackground}
      tabIndex={0}
      {...componentProps(props)}>
      {icon && (
        <IconWrapper>
          <Icon
            name={icon}
            size="large"
            displayVariant={cta && !(isHovering || isFocused)}
          />
        </IconWrapper>
      )}
      {image && <StyledImage image={image} />}
      <Heading as={headingTag}>
        {headingBehaviour.url ? (
          <HeadingLink
            href={headingBehaviour.url}
            onClick={headingBehaviour.onClick}
            removeVisitedStyle>
            {heading}
          </HeadingLink>
        ) : (
          heading
        )}
      </Heading>
      {body && <Body>{body}</Body>}
      <CtaWrapper>
        <PrimaryCta cta={cta} innerRef={ctaRef} />
      </CtaWrapper>
      {secondaryCta && (
        <SecondaryCtaWrapper>
          <SecondaryCta cta={secondaryCta} />
        </SecondaryCtaWrapper>
      )}
      {link && !secondaryCta && (
        <SecondaryLink
          href={link.url}
          onClick={link.onClick}
          color={colors.core01}
          removeVisitedStyle>
          {link.text}
        </SecondaryLink>
      )}
    </ProductCardContainer>
  );
};

export default applyComponentTheme(ProductCard, 'ProductCard');
